import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Settings,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  AlertTriangle,
  Info,
  Zap,
  Brain,
  Sparkles,
  Network,
  Search,
  Code,
  DollarSign,
  ToggleLeft,
  ToggleRight,
  Check,
  X
} from 'lucide-react';

const AIModelManagement = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showApiKey, setShowApiKey] = useState({});
  const [editingApiKey, setEditingApiKey] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  // Provider icons mapping
  const providerIcons = {
    brain: Brain,
    sparkles: Sparkles,
    zap: Zap,
    search: Search,
    network: Network,
    code: Code
  };

  // Load configuration
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/ai-models', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setConfig(response.data.data);
      // Select first enabled provider by default
      const firstEnabled = Object.entries(response.data.data.providers).find(([_, p]) => p.enabled);
      if (firstEnabled) {
        setSelectedProvider(firstEnabled[0]);
      }
    } catch (error) {
      console.error('Failed to load AI model config:', error);
      showMessage('error', 'Failed to load AI model configuration');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleProviderToggle = async (providerId) => {
    try {
      const provider = config.providers[providerId];
      const response = await axios.put(
        `/api/ai-models/providers/${providerId}`,
        { enabled: !provider.enabled },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setConfig(prev => ({
        ...prev,
        providers: {
          ...prev.providers,
          [providerId]: response.data.data
        }
      }));
      
      showMessage('success', `${provider.name} ${!provider.enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Failed to toggle provider:', error);
      showMessage('error', 'Failed to update provider status');
    }
  };

  const handleApiKeyUpdate = async (providerId) => {
    try {
      setSaving(true);
      const apiKey = editingApiKey[providerId];
      
      if (!apiKey || apiKey.trim() === '') {
        showMessage('error', 'API key cannot be empty');
        return;
      }

      await axios.put(
        `/api/ai-models/providers/${providerId}/api-key`,
        { apiKey },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setConfig(prev => ({
        ...prev,
        providers: {
          ...prev.providers,
          [providerId]: {
            ...prev.providers[providerId],
            apiKey
          }
        }
      }));
      
      setEditingApiKey(prev => ({ ...prev, [providerId]: '' }));
      showMessage('success', 'API key updated successfully');
    } catch (error) {
      console.error('Failed to update API key:', error);
      showMessage('error', 'Failed to update API key');
    } finally {
      setSaving(false);
    }
  };

  const handleModelToggle = async (providerId, modelId) => {
    try {
      const model = config.providers[providerId].models.find(m => m.id === modelId);
      const response = await axios.put(
        `/api/ai-models/providers/${providerId}/models/${modelId}`,
        { enabled: !model.enabled },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setConfig(prev => ({
        ...prev,
        providers: {
          ...prev.providers,
          [providerId]: {
            ...prev.providers[providerId],
            models: prev.providers[providerId].models.map(m =>
              m.id === modelId ? response.data.data : m
            )
          }
        }
      }));
      
      showMessage('success', `${model.name} ${!model.enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Failed to toggle model:', error);
      showMessage('error', 'Failed to update model status');
    }
  };

  const handleSettingsUpdate = async (updates) => {
    try {
      setSaving(true);
      const response = await axios.put(
        '/api/ai-models/settings',
        updates,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setConfig(prev => ({
        ...prev,
        settings: response.data.data
      }));
      
      showMessage('success', 'Settings updated successfully');
    } catch (error) {
      console.error('Failed to update settings:', error);
      showMessage('error', 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load configuration</p>
        </div>
      </div>
    );
  }

  const provider = selectedProvider ? config.providers[selectedProvider] : null;
  const ProviderIcon = provider ? providerIcons[provider.icon] || Settings : Settings;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Settings className="mr-3 h-8 w-8 text-blue-600" />
          AI Model Management
        </h1>
        <p className="text-gray-600 mt-2">
          Configure global AI service providers and their models
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 border border-red-200' :
          'bg-blue-50 border border-blue-200'
        }`}>
          <p className={`${
            message.type === 'success' ? 'text-green-800' :
            message.type === 'error' ? 'text-red-800' :
            'text-blue-800'
          }`}>
            {message.text}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Provider List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Providers</h2>
              <p className="text-sm text-gray-600 mt-1">
                {Object.values(config.providers).filter(p => p.enabled).length} of {Object.keys(config.providers).length} enabled
              </p>
            </div>
            <div className="p-2">
              {Object.entries(config.providers)
                .sort((a, b) => a[1].priority - b[1].priority)
                .map(([id, prov]) => {
                  const Icon = providerIcons[prov.icon] || Settings;
                  return (
                    <button
                      key={id}
                      onClick={() => setSelectedProvider(id)}
                      className={`w-full text-left p-3 rounded-lg mb-2 transition-colors ${
                        selectedProvider === id
                          ? 'bg-blue-50 border-2 border-blue-500'
                          : 'hover:bg-gray-50 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Icon className={`h-5 w-5 mr-2 text-${prov.color}-600`} />
                          <div>
                            <div className="font-medium text-gray-900">{prov.name}</div>
                            <div className="text-xs text-gray-500">{prov.models.length} models</div>
                          </div>
                        </div>
                        {prov.enabled ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Provider Details */}
        <div className="lg:col-span-3">
          {provider && (
            <div className="space-y-6">
              {/* Provider Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <ProviderIcon className={`h-8 w-8 mr-3 text-${provider.color}-600`} />
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{provider.name}</h2>
                      <p className="text-gray-600 mt-1">{provider.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleProviderToggle(selectedProvider)}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                      provider.enabled
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {provider.enabled ? (
                      <>
                        <ToggleRight className="h-5 w-5 mr-2" />
                        Enabled
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="h-5 w-5 mr-2" />
                        Disabled
                      </>
                    )}
                  </button>
                </div>

                {/* API Key */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key
                  </label>
                  <div className="flex space-x-2">
                    <div className="flex-1 relative">
                      <input
                        type={showApiKey[selectedProvider] ? 'text' : 'password'}
                        value={editingApiKey[selectedProvider] || provider.apiKey || ''}
                        onChange={(e) => setEditingApiKey(prev => ({
                          ...prev,
                          [selectedProvider]: e.target.value
                        }))}
                        placeholder="Enter API key..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        onClick={() => setShowApiKey(prev => ({
                          ...prev,
                          [selectedProvider]: !prev[selectedProvider]
                        }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showApiKey[selectedProvider] ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => handleApiKeyUpdate(selectedProvider)}
                      disabled={saving || !editingApiKey[selectedProvider]}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </button>
                  </div>
                  {provider.apiKey && (
                    <p className="text-sm text-green-600 mt-2 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      API key configured
                    </p>
                  )}
                </div>

                {/* Base URL */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base URL
                  </label>
                  <input
                    type="text"
                    value={provider.baseUrl}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>
              </div>

              {/* Models */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Available Models</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {provider.models.filter(m => m.enabled).length} of {provider.models.length} models enabled
                  </p>
                </div>
                <div className="p-4">
                  <div className="space-y-4">
                    {provider.models.map((model) => (
                      <div
                        key={model.id}
                        className={`border-2 rounded-lg p-4 transition-all ${
                          model.enabled
                            ? 'border-green-200 bg-green-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <h4 className="text-lg font-semibold text-gray-900">{model.name}</h4>
                              {model.recommended && (
                                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{model.description}</p>
                            
                            {/* Model Details */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                              <div>
                                <p className="text-xs text-gray-500">Context Window</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {model.contextWindow.toLocaleString()} tokens
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Max Output</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {model.maxTokens.toLocaleString()} tokens
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Input Cost</p>
                                <p className="text-sm font-medium text-gray-900 flex items-center">
                                  <DollarSign className="h-3 w-3" />
                                  {model.costPer1kInputTokens.toFixed(4)}/1K
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Output Cost</p>
                                <p className="text-sm font-medium text-gray-900 flex items-center">
                                  <DollarSign className="h-3 w-3" />
                                  {model.costPer1kOutputTokens.toFixed(4)}/1K
                                </p>
                              </div>
                            </div>

                            {/* Capabilities */}
                            <div className="mt-3">
                              <p className="text-xs text-gray-500 mb-1">Capabilities</p>
                              <div className="flex flex-wrap gap-2">
                                {model.capabilities.map((cap) => (
                                  <span
                                    key={cap}
                                    className="px-2 py-1 bg-white border border-gray-300 text-gray-700 text-xs rounded"
                                  >
                                    {cap}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Toggle Button */}
                          <button
                            onClick={() => handleModelToggle(selectedProvider, model.id)}
                            className={`ml-4 flex items-center px-3 py-2 rounded-lg font-medium transition-colors ${
                              model.enabled
                                ? 'bg-green-600 text-white hover:bg-green-700'
                                : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                            }`}
                          >
                            {model.enabled ? (
                              <>
                                <Check className="h-4 w-4 mr-1" />
                                Enabled
                              </>
                            ) : (
                              <>
                                <X className="h-4 w-4 mr-1" />
                                Disabled
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIModelManagement;

